import { useCallback, useMemo, useRef, useState } from 'react';
import { IPanelGroupProps, PanelGroupHandle, PanelLayout } from '@cbridge-tools/resizable-panels';
import { useDispatch, useSelector } from '@cbridge-utils/react-redux';
import { parseJsonSetting, UserSettingsAction, userSettingSelector } from '@cbridge-packages/user-settings';
import {
  IPanelLayoutSetting, isPanelLayoutSetting, isSamePanelLayout, panelSetKey,
} from '../_helpers/panelLayoutSetting';

export type IPersistedPanelLayoutProps = Pick<IPanelGroupProps, 'groupRef' | 'onLayoutChanged'>;

const NO_PERSISTENCE: IPersistedPanelLayoutProps = {};

/**
 * Persists a PanelGroup layout in the profile settings under `settingKey` (LB-4992); spread the
 * result onto the group. Every panel of the group needs a stable `id`.
 *
 * The setting holds one layout per set of mounted panels. The group reports its layout after
 * mount and whenever a panel appears or disappears; that first report for a panel set restores
 * the saved layout through `setLayout` (inside the group's layout effect, so nothing paints with
 * default sizes). Later reports for the same set are user resizes and are written through.
 * Nothing is read or written without a key.
 */
export const usePersistedPanelLayout = (settingKey?: string): IPersistedPanelLayoutProps => {
  const dispatch = useDispatch();
  const savedRaw = useSelector(userSettingSelector, { settingKey: settingKey ?? '' });
  // Frozen at mount: the store echoes every save and must not feed back into the group.
  const [initial] = useState<IPanelLayoutSetting>(() => parseJsonSetting(savedRaw, isPanelLayoutSetting) ?? {});
  const layoutsRef = useRef(initial);
  const restoredSetRef = useRef<string | null>(null);
  const groupRef = useRef<PanelGroupHandle | null>(null);

  const onLayoutChanged = useCallback((layout: PanelLayout) => {
    if (!settingKey) {
      return;
    }

    const setKey = panelSetKey(layout);
    const saved = layoutsRef.current[setKey];

    if (restoredSetRef.current !== setKey) {
      restoredSetRef.current = setKey;
      if (saved && !isSamePanelLayout(saved, layout)) {
        groupRef.current?.setLayout(saved);
      }

      return;
    }

    if (saved && isSamePanelLayout(saved, layout)) {
      return;
    }

    layoutsRef.current = { ...layoutsRef.current, [setKey]: layout };
    dispatch(UserSettingsAction.saveSetting({ key: settingKey, value: JSON.stringify(layoutsRef.current) }));
  }, [dispatch, settingKey]);

  return useMemo(
    () => (settingKey ? { groupRef, onLayoutChanged } : NO_PERSISTENCE),
    [settingKey, onLayoutChanged],
  );
};
